"use client";

import { useLang } from "@/contexts/language-context";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { useState } from "react";

const FAQ = [
  {
    q: {
      en: "Does it really work without WiFi?",
      es: "¿De verdad funciona sin WiFi?",
    },
    a: {
      en: "For cash, yes. If the signal drops, the POS switches to cash mode: cash sales keep ringing and are saved on the iPad, then sync on their own when you reconnect — anything that can't sync is flagged for review. Card payments, tabs and online orders need a connection (a phone hotspot works). The KDS keeps the tickets it already has; new ones arrive when you're back online.",
      es: "Para efectivo, sí. Si se cae la señal, el POS pasa a modo efectivo: las ventas en efectivo siguen y se guardan en el iPad, y se sincronizan solas al reconectar — lo que no pueda sincronizar queda marcado para revisión. Los pagos con tarjeta, las tabs y los pedidos online necesitan conexión (un hotspot del celular sirve). El KDS mantiene los tickets que ya tenía; los nuevos llegan cuando vuelves online.",
    },
  },
  {
    q: {
      en: "Is the North terminal portable enough for a food truck?",
      es: "¿El terminal North es lo suficientemente portátil?",
    },
    a: {
      en: "Yes. It's a compact, handheld terminal with its own battery, so you can hand it out the window or take it to a customer at a picnic table. It's a $249 one-time purchase, set up with your Redy payments account.",
      es: "Sí. Es un terminal compacto, de mano, con batería propia, así que lo pasas por la ventana o lo llevas a una mesa. Es una compra única de $249, configurada con tu cuenta de pagos de Redy.",
    },
  },
  {
    q: {
      en: "Can my customers use dual pricing on a food truck?",
      es: "¿Puedo usar dual pricing en un food truck?",
    },
    a: {
      en: "Yes. Your menu shows the card price, and customers who pay cash get a discount — the receipt shows it as its own \"Cash discount\" line. Rules vary by state and card network, so post the signage your state requires; that part is on you, not on Redy.",
      es: "Sí. Tu menú muestra el precio con tarjeta, y quien paga en efectivo recibe un descuento — el recibo lo muestra como línea propia de \"Descuento en efectivo\". Las reglas cambian según el estado y la red de tarjetas, así que pon los avisos que exige tu estado; esa parte te toca a ti, no a Redy.",
    },
  },
  {
    q: {
      en: "How fast can I be live?",
      es: "¿Qué tan rápido puedo arrancar?",
    },
    a: {
      en: "Sign up and build your menu, and you can start ringing cash sales right away. Card payments start once your payments account is set up and your terminal arrives.",
      es: "Te registras y armas tu menú, y puedes empezar a cobrar en efectivo de inmediato. Los pagos con tarjeta arrancan cuando tu cuenta de pagos está lista y llega tu terminal.",
    },
  },
  {
    q: {
      en: "What if I have multiple trucks?",
      es: "¿Qué pasa si tengo varios trucks?",
    },
    a: {
      en: "Each truck is its own location with its own iPad, terminal, menu hours, and sales reports. You manage all of them from a single admin dashboard. Switch trucks like switching tabs.",
      es: "Cada truck es su propia ubicación con su iPad, terminal, horario de menú y reportes. Manejas todos desde un solo admin. Cambias entre trucks como entre tabs.",
    },
  },
];

export default function FoodTruckQsrFaq() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[820px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            FAQ
          </div>
          <h2 className="text-[36px] md:text-[52px] font-black leading-[1.04] tracking-tight text-redy-charcoal">
            {t(
              "Questions from food truck operators.",
              "Preguntas de operadores de food truck."
            )}
          </h2>
        </motion.div>

        <div className="space-y-3">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="border-b border-black/[0.06] py-2"
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-start justify-between gap-6 text-left py-4 group"
                >
                  <h3 className="text-lg md:text-xl font-bold text-redy-charcoal group-hover:text-redy-orange transition-colors">
                    {item.q[lang as "en" | "es"] ?? item.q.en}
                  </h3>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex-shrink-0 mt-1"
                  >
                    <Plus className="w-5 h-5 text-redy-charcoal/60 group-hover:text-redy-orange" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 pr-12 text-base text-redy-secondary leading-relaxed">
                        {item.a[lang as "en" | "es"] ?? item.a.en}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
